import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  ItemCostAllocation,
  type ItemCostAllocationConstructor,
} from '../ItemCostAllocation'

// REF column: PO-backed item (label "PO {number}", links by uuid;
// vendorName prefixes the description). Local value object.
interface CreditCardItemPoRef {
  number: string
  uuid: string
  vendorName: string
}

// REF column: expense-backed item (label "RFE {expenseReportId}";
// merchant prefixes the description). Local value object.
interface CreditCardItemExpenseRef {
  expenseReportId: number
  uuid: string
  merchant: string
}

// Attachment reference from the item payload ({ id, uuid, name } only) —
// "view" packing-slip link + edit-form attachment list.
interface PackingSlipRef {
  id: number
  uuid: string
  name: string
}

// Populated when total cost / currency diverge from the original —
// drives the red change flag on the matching screen (BHV-038).
interface UnitCostDiffLog {
  originalUnitCost: number
  originalCurrencyLabel: string
}

export interface CreditCardItemConstructor {
  id: number
  creditcardId: number
  description: string
  totalCost: number
  currency: CurrencyConstructor
  receivedOn: ISODateString
  costAllocations: ItemCostAllocationConstructor[]
  packingSlips: PackingSlipRef[]
  po: Nullable<CreditCardItemPoRef>
  expense: Nullable<CreditCardItemExpenseRef>
  matchedStatementItemIds: number[] // from matched_statement_items
  isMovable: boolean // from creditcard_is_editable
  unitCostDiffLog: Nullable<UnitCostDiffLog>
}

export class CreditCardItem {
  readonly id: number
  readonly creditcardId: number
  readonly description: string
  readonly totalCost: number
  readonly currency: Currency
  readonly receivedOn: Date
  readonly costAllocations: ItemCostAllocation[]
  readonly packingSlips: PackingSlipRef[]
  readonly po: Nullable<CreditCardItemPoRef>
  readonly expense: Nullable<CreditCardItemExpenseRef>
  readonly matchedStatementItemIds: number[]
  readonly isMovable: boolean
  readonly unitCostDiffLog: Nullable<UnitCostDiffLog>

  constructor(data: CreditCardItemConstructor) {
    this.id = data.id
    this.creditcardId = data.creditcardId
    this.description = data.description
    this.totalCost = data.totalCost
    this.currency = new Currency(data.currency)
    this.receivedOn = new Date(data.receivedOn)
    this.costAllocations = data.costAllocations.map(
      (allocation) => new ItemCostAllocation(allocation)
    )
    this.packingSlips = data.packingSlips
    this.po = data.po
    this.expense = data.expense
    this.matchedStatementItemIds = data.matchedStatementItemIds
    this.isMovable = data.isMovable
    this.unitCostDiffLog = data.unitCostDiffLog
  }

  // Unmatched items are fully editable / archivable (BHV-013/019)
  get isEditable(): boolean {
    return this.matchedStatementItemIds.length === 0
  }

  // "PO 1042" / "RFE 29" REF label; null for manual items (BHV-011)
  get refLabel(): Nullable<string> {
    if (this.po) return `PO ${this.po.number}`
    if (this.expense) return `RFE ${this.expense.expenseReportId}`
    return null
  }

  // Vendor/merchant-prefixed description, e.g. "Acme - Widgets" (BHV-011)
  get descriptionLabel(): string {
    const prefix = this.po?.vendorName ?? this.expense?.merchant
    return prefix ? `${prefix} - ${this.description}` : this.description
  }

  // "6000 - Supplies" / "Multiple" / "-" account-code column (BHV-011)
  get accountCodeLabel(): string {
    if (this.costAllocations.length === 0) return '-'
    if (this.costAllocations.length > 1) return 'Multiple'
    return this.costAllocations[0].accountCodeLabel
  }

  // "N suggested match(es)" chip on the unmatched pane (BHV-037)
  get suggestedMatchCount(): number {
    return this.matchedStatementItemIds.length
  }

  // Red change flag when cost/currency diverge from original (BHV-038)
  get hasCostChange(): boolean {
    if (!this.unitCostDiffLog) return false
    return (
      this.unitCostDiffLog.originalUnitCost !== this.totalCost ||
      this.unitCostDiffLog.originalCurrencyLabel !== this.currency.name
    )
  }

  // "Invalid Currency" gating on match selection (BHV-032)
  hasInvalidCurrency(cardCurrencyId: number): boolean {
    return this.currency.id !== cardCurrencyId
  }
}
